// needs to support both client and server because its used in both server and client components

import React from "react";
import Link from "next/link";
import { ComponentProps } from "react";

import { optimizeURL } from "@/utils/url";
import { LinkNoPrefetch } from "@/components/LinkNoPrefetch";

interface LinkProps extends ComponentProps<"a"> {
  href?: string;
  children: React.ReactNode;
  target: string;
  alwaysRenderChildren?: boolean;
  /**
   * Creates a link with no prefetching abilities... which Next Links per default have
   */
  disablePrefetch?: boolean;
  /**
   * Creates a link which triggers prefetching only on hover... Next Links prefetch per default as soon as link is visible
   */
  onlyPrefetchOnHover?: boolean;
  className?: string;
}

/*
 *
 * @param alwaysRenderChildren - set to true if you want to render children if href is undefined
 * @returns
 */
export const CustomLink = React.forwardRef<HTMLAnchorElement, LinkProps>(
  (
    {
      children,
      href,
      disablePrefetch = false,
      onlyPrefetchOnHover = false,
      className,
      alwaysRenderChildren = false,
      ...props
    },
    ref,
  ) => {
    const rel = props.target === "_blank" ? "noopener noreferrer" : undefined;

    if (!href || href.length === 0) {
      // Return nothing if link is not set
      if (alwaysRenderChildren) {
        return children;
      }
      return null;
    }

    const extraprops = {
      ...(rel && { rel }),
      ...props,
      ...(props.target !== "" ? { target: props.target } : { target: "_self" }),
    };

    return disablePrefetch ? (
      // eslint-disable-next-line react/forbid-elements
      <LinkNoPrefetch
        ref={ref}
        {...extraprops}
        href={optimizeURL(href)!}
        className={className}
      >
        {children}
      </LinkNoPrefetch>
    ) : !onlyPrefetchOnHover ? (
      // eslint-disable-next-line react/forbid-elements
      <Link
        ref={ref}
        href={optimizeURL(href)!}
        prefetch={false}
        className={className}
        {...extraprops}
      >
        {children}
      </Link>
    ) : (
      // eslint-disable-next-line react/forbid-elements
      <Link
        ref={ref}
        href={optimizeURL(href)!}
        className={className}
        {...extraprops}
      >
        {children}
      </Link>
    );
  },
);

CustomLink.displayName = "CustomLink";
